import { Controller } from "@hotwired/stimulus";

import { prefersReducedMotion } from "~/lib/viewport";
import { Clock, fitStage, whileInView } from "~/lib/web/loop";

const STAGE_WIDTH = 560;

/**
 * The popup and the toolbar badge above it are both at x=225 or further right.
 * Cropping from the left therefore removes only the traffic lights and the left
 * of the address pill, and lets the popup render 44% larger on a phone.
 */
const MIN_SCALE = 0.88;

const SWAP_OUT_MS = 240;

const RESIZE_MS = 520;

/**
 * Between one row settling and the next. Short enough that the rows read as
 * one action rather than as a queue being worked through.
 */
const FILE_GAP_MS = 560;

export default class WebFetcherController extends Controller<HTMLElement> {
  static override targets = [
    "stage",
    "box",
    "inner",
    "body",
    "status",
    "mark",
    "badge",
    "restTemplate",
    "workingTemplate",
  ];
  declare readonly stageTarget: HTMLElement;
  declare readonly hasStageTarget: boolean;
  declare readonly boxTarget: HTMLElement;
  declare readonly hasBoxTarget: boolean;
  declare readonly innerTarget: HTMLElement;
  declare readonly hasInnerTarget: boolean;
  declare readonly bodyTarget: HTMLElement;
  declare readonly hasBodyTarget: boolean;
  declare readonly statusTarget: HTMLElement;
  declare readonly markTarget: HTMLElement;
  declare readonly badgeTarget: HTMLElement;
  declare readonly restTemplateTarget: HTMLTemplateElement;
  declare readonly workingTemplateTarget: HTMLTemplateElement;

  private clock = new Clock();
  private stop: (() => void) | null = null;
  private unfit: (() => void) | null = null;
  private found = 0;

  override connect(): void {
    if (
      !this.hasStageTarget ||
      !this.hasBoxTarget ||
      !this.hasInnerTarget ||
      !this.hasBodyTarget
    ) {
      return;
    }
    this.unfit = fitStage(
      this.element,
      this.stageTarget,
      STAGE_WIDTH,
      MIN_SCALE,
    );

    if (prefersReducedMotion()) {
      this.badgeTarget.classList.add("is-on");
      for (const item of this.bodyTarget.querySelectorAll("[data-rise]")) {
        item.classList.add("is-on");
      }
      return;
    }

    /* Counted off the markup rather than stated here as well: the badge and the
       vendor's "N missing" pill already come from FETCHER_RECEIPTS on the
       server. */
    this.found =
      this.workingTemplateTarget.content.querySelectorAll(
        "[data-receipt-row]",
      ).length;

    this.stop = whileInView(
      this.element,
      () => {
        this.settle();
        this.run();
      },
      () => this.clock.clear(),
    );
  }

  override disconnect(): void {
    this.stop?.();
    this.stop = null;
    this.unfit?.();
    this.unfit = null;
    this.clock.clear();
  }

  /**
   * `inner` is never height constrained, so its offsetHeight is always the
   * natural height of the current content. Writing that onto the box animates
   * the box to fit it. Nothing else sets a height.
   */
  private fitBox(): void {
    this.boxTarget.style.height = `${this.innerTarget.offsetHeight}px`;
  }

  private setStatus(text: string): void {
    const status = this.statusTarget;
    status.classList.add("is-out");
    this.clock.at(190, () => {
      status.textContent = text;
      status.classList.remove("is-out");
      status.classList.add("is-in");
      void status.offsetWidth;
      status.classList.remove("is-in");
    });
  }

  /**
   * Anything reading the new content has to be scheduled inside `after`.
   * Scheduled on its own it is ordered only by arithmetic, and if it runs before
   * the content is written its query finds nothing and the beat is skipped with
   * no error.
   */
  private setBody(
    template: HTMLTemplateElement,
    statusText: string,
    after?: () => void,
  ): void {
    this.bodyTarget.classList.add("is-hidden");
    this.statusTarget.classList.add("is-out");
    this.clock.at(SWAP_OUT_MS, () => {
      this.bodyTarget.replaceChildren(template.content.cloneNode(true));
      this.statusTarget.textContent = statusText;
      this.statusTarget.classList.remove("is-out");
      this.fitBox();
      this.bodyTarget.classList.remove("is-hidden");
      after?.();
    });
  }

  /**
   * One entrance for everything in the card: fade up from 7px low, staggered in
   * document order. There is no per-element fade anywhere else.
   */
  private riseIn(step: number): void {
    const items = this.bodyTarget.querySelectorAll("[data-rise]");
    items.forEach((item, index) => {
      this.clock.at(SWAP_OUT_MS + 20 + index * step, () =>
        item.classList.add("is-on"),
      );
    });
  }

  /**
   * The card is open by default, and the loop returns to this state through the
   * same content swap as every other beat.
   */
  private settle(): void {
    const body = this.bodyTarget;
    body.replaceChildren(this.restTemplateTarget.content.cloneNode(true));
    for (const item of body.querySelectorAll("[data-rise]")) {
      item.classList.add("is-on");
    }
    this.statusTarget.textContent = this.restingStatus;
    this.badgeTarget.classList.add("is-on");
    this.markTarget.classList.remove("is-working");
    this.boxTarget.style.transition = "none";
    this.fitBox();
    void this.boxTarget.offsetWidth;
    this.boxTarget.style.transition = `height ${RESIZE_MS}ms var(--rf-ease-box), opacity 420ms var(--rf-ease)`;
  }

  private run(): void {
    let t = 1700;

    this.clock.at(t, () => {
      this.markTarget.classList.add("is-working");
      this.setBody(this.workingTemplateTarget, this.foundStatus, () =>
        this.riseIn(110),
      );
    });

    /* Uploading. No button and no resize: the rows arrive already working, so
       this beat only changes the status text. Scheduled shortly after the last
       row settles, so the three spinners are never turning with no status of
       their own. */
    t += SWAP_OUT_MS + RESIZE_MS + 1000;
    this.clock.at(t, () => this.setStatus(this.uploadingStatus));

    /* Resolved one at a time, from the second row on: the first arrives already
       done. The box height does not change between the rows arriving and the
       swap back. Bounded by `found` too, so a fourth receipt cannot be counted
       and then left spinning. */
    t += 1000;
    for (let row = 1; row < this.found; row++) {
      const index = row;
      this.clock.at(t + (index - 1) * FILE_GAP_MS, () => this.finishRow(index));
    }
    // Scheduled to finish with the last row, whichever one that is.
    this.clock.at(t + Math.max(0, this.found - 2) * FILE_GAP_MS, () => {
      this.markTarget.classList.remove("is-working");
      this.badgeTarget.classList.remove("is-on");
      this.setStatus(this.uploadedStatus);
    });

    // Back to the idle state through the same content swap every other beat
    // uses, so the card never disappears. The badge is shown again with it,
    // since there are receipts to collect again.
    t += 2200;
    this.clock.at(t, () => {
      this.badgeTarget.classList.add("is-on");
      this.setBody(this.restTemplateTarget, this.restingStatus, () =>
        this.riseIn(90),
      );
    });
    this.clock.at(t + SWAP_OUT_MS + RESIZE_MS + 1200, () => this.run());
  }

  private finishRow(index: number): void {
    const row = this.bodyTarget.querySelector(`[data-receipt-row="${index}"]`);
    if (row === null) return;
    row.classList.remove("is-loading");
    row.classList.add("is-done");
  }

  // The four status lines are rendered on the server, so the copy is translated
  // and defined once rather than once per language here.

  private get restingStatus(): string {
    return this.element.dataset["statusResting"] ?? "";
  }

  private get foundStatus(): string {
    return this.element.dataset["statusFound"] ?? "";
  }

  private get uploadingStatus(): string {
    return this.element.dataset["statusUploading"] ?? "";
  }

  private get uploadedStatus(): string {
    return this.element.dataset["statusUploaded"] ?? "";
  }
}
